/**
 * Willow & Dove — Cart Page JavaScript
 * Handles: quantity changes, item removal, cross-sell add-to-cart,
 *          shipping progress bar, empty cart state, cart badge updates
 *
 * Uses Shopify Cart AJAX API:
 *   POST /cart/add.js       — add items
 *   POST /cart/change.js    — update quantity / remove
 *   GET  /cart.js           — fetch full cart
 */

(function () {
  'use strict';

  var FREE_SHIPPING_THRESHOLD = 25000; // $250 CAD in cents

  // ── Utility ──────────────────────────────────────────────────
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fetchJSON(url, options) {
    options = options || {};
    options.headers = options.headers || {};
    options.headers['Content-Type'] = 'application/json';
    options.headers['Accept'] = 'application/json';
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed: ' + res.status);
      return res.json();
    });
  }

  // ── Get fresh cart data ──────────────────────────────────────
  function getCart() {
    return fetchJSON('/cart.js');
  }

  // ── Change item quantity (or remove if qty = 0) ──────────────
  function changeItem(key, quantity) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  // ── Add item to cart ─────────────────────────────────────────
  function addItem(variantId, quantity) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }]
      })
    });
  }

  // ── Update the cart badge in the nav ─────────────────────────
  function updateCartBadge(count) {
    var badges = document.querySelectorAll('[data-cart-count], #navCartBadge, .cart-badge');
    badges.forEach(function (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? '' : 'none';
    });
  }

  // ── Update the item count label ──────────────────────────────
  function updateItemCount(count) {
    var label = document.getElementById('itemCountLabel');
    if (label) {
      label.textContent = count + (count === 1 ? ' item' : ' items');
    }
  }

  // ── Update shipping progress bar ─────────────────────────────
  function updateShippingProgress(totalCents) {
    var progressSection = document.getElementById('shippingProgress');
    var shippingMsg = document.getElementById('shippingMsg');
    var progressFill = document.getElementById('progressFill');
    var shippingValue = document.getElementById('summaryShipping');

    if (!progressSection) return;

    if (totalCents >= FREE_SHIPPING_THRESHOLD) {
      progressSection.style.display = 'none';
      if (shippingValue) {
        shippingValue.textContent = 'Free';
        shippingValue.style.color = 'var(--primary)';
        shippingValue.style.fontWeight = '600';
      }
    } else if (totalCents > 0) {
      var remaining = FREE_SHIPPING_THRESHOLD - totalCents;
      progressSection.style.display = 'block';
      if (shippingMsg) {
        shippingMsg.textContent = "You're " + formatMoney(remaining) + ' away from free shipping!';
        shippingMsg.classList.remove('free');
      }
      if (progressFill) {
        var pct = Math.min((totalCents / FREE_SHIPPING_THRESHOLD) * 100, 100);
        progressFill.style.width = pct + '%';
      }
      if (shippingValue) {
        shippingValue.textContent = 'Calculated at checkout';
        shippingValue.style.color = '';
        shippingValue.style.fontWeight = '500';
      }
    }
  }

  // ── Update totals from cart data ─────────────────────────────
  function updateTotals(cart) {
    var subtotalInline = document.getElementById('cartSubtotalInline');
    var summarySubtotal = document.getElementById('summarySubtotal');
    var summaryTotal = document.getElementById('summaryTotal');

    if (subtotalInline) subtotalInline.textContent = formatMoney(cart.total_price);
    if (summarySubtotal) summarySubtotal.textContent = formatMoney(cart.total_price);
    if (summaryTotal) summaryTotal.textContent = formatMoney(cart.total_price);

    updateShippingProgress(cart.total_price);
    updateCartBadge(cart.item_count);
    updateItemCount(cart.item_count);
  }

  // ── Show empty cart state ────────────────────────────────────
  function showEmptyCart() {
    var container = document.getElementById('cartContainer');
    if (!container) return;

    container.innerHTML =
      '<div class="empty-cart" style="width:100%;">' +
        '<svg style="width:64px;height:64px;margin:0 auto 24px;color:rgba(26,26,26,0.2);" fill="none" stroke="currentColor" stroke-width="1" viewBox="0 0 24 24">' +
          '<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>' +
        '</svg>' +
        '<h2>Your cart is empty</h2>' +
        '<p>Looks like you haven\'t added anything to your cart yet.</p>' +
        '<a href="/collections/all">Continue Shopping</a>' +
      '</div>';

    updateItemCount(0);
    updateCartBadge(0);
  }

  // ── Re-render a single cart item's line total ────────────────
  function updateLineItem(itemEl, lineItem) {
    var lineTotalEl = itemEl.querySelector('.cart-item-line-total');
    var qtyDisplay = itemEl.querySelector('[data-qty-display]');
    var unitPriceEl = itemEl.querySelector('.cart-item-unit-price');

    if (lineTotalEl) lineTotalEl.textContent = formatMoney(lineItem.final_line_price);
    if (qtyDisplay) qtyDisplay.textContent = lineItem.quantity;
    if (unitPriceEl) unitPriceEl.textContent = formatMoney(lineItem.original_price) + ' each';

    // Update data attributes on buttons
    var minusBtn = itemEl.querySelector('[data-action="decrease"]');
    var plusBtn = itemEl.querySelector('[data-action="increase"]');
    if (minusBtn) minusBtn.setAttribute('data-qty', lineItem.quantity);
    if (plusBtn) plusBtn.setAttribute('data-qty', lineItem.quantity);
  }

  // ── Animate item removal ─────────────────────────────────────
  function animateRemoval(itemEl, callback) {
    itemEl.style.maxHeight = itemEl.offsetHeight + 'px';
    // Force reflow
    itemEl.offsetHeight;
    itemEl.classList.add('removing');
    setTimeout(function () {
      itemEl.remove();
      if (callback) callback();
    }, 420);
  }

  // ── Handle quantity change ───────────────────────────────────
  function handleQuantityChange(e) {
    var btn = e.target.closest('[data-action="decrease"], [data-action="increase"]');
    if (!btn) return;

    var action = btn.getAttribute('data-action');
    var key = btn.getAttribute('data-key');
    var currentQty = parseInt(btn.getAttribute('data-qty'), 10);
    var newQty;

    if (action === 'decrease') {
      newQty = currentQty - 1;
    } else if (action === 'increase') {
      newQty = currentQty + 1;
    }

    if (newQty < 0) newQty = 0;
    if (newQty > 99) newQty = 99;

    var itemEl = btn.closest('.cart-item');
    if (!itemEl) return;

    // If decrementing to zero, treat as removal
    if (newQty === 0) {
      handleRemoveItem(itemEl, key);
      return;
    }

    // Add loading state
    itemEl.classList.add('is-updating');
    var picker = btn.closest('.qty-picker');
    if (picker) picker.classList.add('is-loading');

    changeItem(key, newQty)
      .then(function (cart) {
        // Find the line item in the updated cart
        var lineItem = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) {
            lineItem = cart.items[i];
            break;
          }
        }

        if (lineItem) {
          updateLineItem(itemEl, lineItem);
        }

        updateTotals(cart);

        // Remove loading state
        itemEl.classList.remove('is-updating');
        if (picker) picker.classList.remove('is-loading');
      })
      .catch(function (err) {
        console.error('Failed to update cart:', err);
        itemEl.classList.remove('is-updating');
        if (picker) picker.classList.remove('is-loading');
      });
  }

  // ── Handle item removal ──────────────────────────────────────
  function handleRemoveItem(itemEl, key) {
    animateRemoval(itemEl, function () {
      changeItem(key, 0)
        .then(function (cart) {
          updateTotals(cart);
          if (cart.item_count === 0) {
            showEmptyCart();
          }
        })
        .catch(function (err) {
          console.error('Failed to remove item:', err);
          // Reload the page as fallback
          window.location.reload();
        });
    });
  }

  // ── Handle remove button click ───────────────────────────────
  function handleRemoveClick(e) {
    var btn = e.target.closest('[data-action="remove"]');
    if (!btn) return;

    var key = btn.getAttribute('data-key');
    var itemEl = btn.closest('.cart-item');
    if (!itemEl || !key) return;

    handleRemoveItem(itemEl, key);
  }

  // ── Handle cross-sell add to cart ────────────────────────────
  function handleCrossSellAdd(e) {
    var btn = e.target.closest('[data-action="add-to-cart"]');
    if (!btn) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var originalText = btn.textContent;
    btn.textContent = 'Adding...';
    btn.disabled = true;

    addItem(variantId, 1)
      .then(function () {
        btn.textContent = 'Added!';
        btn.style.background = 'var(--primary)';
        btn.style.color = 'var(--white, #fff)';

        // Update the cart badge
        return getCart();
      })
      .then(function (cart) {
        updateCartBadge(cart.item_count);

        // Reset button after delay
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
          btn.style.background = '';
          btn.style.color = '';
        }, 2000);
      })
      .catch(function (err) {
        console.error('Failed to add item:', err);
        btn.textContent = 'Error';
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      });
  }

  // ── Initialize event listeners ───────────────────────────────
  function init() {
    var cartContainer = document.getElementById('cartContainer');

    if (cartContainer) {
      // Delegate quantity and remove events to the container
      cartContainer.addEventListener('click', function (e) {
        handleQuantityChange(e);
        handleRemoveClick(e);
      });
    }

    // Cross-sell add-to-cart buttons (outside the cart container)
    document.addEventListener('click', function (e) {
      handleCrossSellAdd(e);
    });
  }

  // ── Run on DOM ready ─────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
